/**
 * Merlin follow-ups — the promises Merlin made on the Quality door and
 * whether it kept them (kanban bf65c1cd; the Pending tab, kanban 43160d8b).
 *
 * Wraps `/api/followups` (app/routes/merlin_followups.py): the reader's own
 * rows, one row's checks and events, and Cancel. Every write goes through
 * the backend FSM, which records an audit event with it. The route's
 * "Check now" (POST /{id}/check) is deliberately not wrapped: the owner kept
 * it off the door (review wxb07n, 2026-10-21); the sweep runs checks when due.
 *
 * Uses the shared axios instance (services/api.ts) like qcpDataRequests.ts.
 */
import api from './api';

const BASE = '/followups';

/** What kind of promise it is (services/merlin_followup/fsm.py FollowupKind). */
export type FollowupKind = 'date' | 'condition';

/** Where a follow-up stands (FollowupStatus). `scheduled` and `checking` are
 *  the open ones; the rest are settled. */
export type FollowupStatus =
  | 'scheduled'
  | 'checking'
  | 'fired'
  | 'satisfied'
  | 'expired'
  | 'cancelled';

export const OPEN_FOLLOWUP_STATUSES: readonly FollowupStatus[] = ['scheduled', 'checking'];

/** What the promise hangs on (AnchorKind). */
export type FollowupAnchorKind = 'signal_thread' | 'conversation';

/** One follow-up as `GET /api/followups` returns it (FollowupRead). */
export interface MerlinFollowupRead {
  id: string;
  owner_user_id: string;
  owner_name: string | null;
  /** The door the promise was made on: `qcp_portal` is Jess's chat,
   *  `thread_investigator` is Merlin's own nightly plan. */
  profile_id: string;
  kind: FollowupKind;
  kind_label: string;
  /** What Merlin said it would do ("I'll tell you how many pails it made"). */
  promise_text: string;
  /** When / under what condition ("when fill 66804 posts"). */
  condition_text: string;
  anchor_kind: FollowupAnchorKind;
  anchor_id: string | null;
  /** The QT number of the thread it hangs on, when it hangs on one. */
  anchor_ref: string | null;
  conversation_id: string | null;
  data_sources: string[];
  status: FollowupStatus;
  due_at: string | null;
  expires_at: string;
  check_count: number;
  failed_check_count: number;
  last_checked_at: string | null;
  /** `chat` (promised in a conversation) or `backfill` (the promise audit). */
  origin: string;
  resolution_note: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  promised_at: string;
  created_at: string;
  updated_at: string;
}

/** One check (CheckRead): a run of the small-model verifier. */
export interface MerlinFollowupCheckRead {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  trigger: string;
  queued_at: string;
  started_at: string | null;
  finished_at: string | null;
  model: string | null;
  verdict: string | null;
  note: string | null;
  cost_usd: number | null;
  error: string | null;
  audit_conversation_id: string | null;
  delivered_message_id: string | null;
  delivered_notification_id: string | null;
}

export interface MerlinFollowupEventRead {
  id: string;
  kind: string;
  actor: string;
  payload: Record<string, unknown>;
  check_id: string | null;
  created_at: string;
}

export interface MerlinFollowupDetail extends MerlinFollowupRead {
  checks: MerlinFollowupCheckRead[];
  events: MerlinFollowupEventRead[];
}

export interface ListFollowupsParams {
  /** `all` lists every owner's rows (admins only); default `mine`. */
  scope?: 'mine' | 'all';
  open_only?: boolean;
  status?: FollowupStatus[];
  thread_id?: string;
}

/** The reader's follow-ups, newest first. */
export async function listFollowups(params: ListFollowupsParams = {}): Promise<MerlinFollowupRead[]> {
  const { data } = await api.get<MerlinFollowupRead[]>(BASE, {
    params,
    // axios serialises arrays as status[]=…; FastAPI reads repeated `status=`.
    paramsSerializer: { indexes: null },
  });
  return data;
}

/** One follow-up with its checks and events. */
export async function getFollowup(id: string): Promise<MerlinFollowupDetail> {
  const { data } = await api.get<MerlinFollowupDetail>(`${BASE}/${encodeURIComponent(id)}`);
  return data;
}

/** Withdraw a follow-up. The reason is recorded on the audit event. */
export async function cancelFollowup(id: string, reason: string): Promise<MerlinFollowupRead> {
  const { data } = await api.post<MerlinFollowupRead>(`${BASE}/${encodeURIComponent(id)}/cancel`, {
    reason,
  });
  return data;
}

